import { FieldModel, PracticeTemplateDetail, ProgramBenchmarkName, ProgramTask } from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitDataGrid } from '../TraitDataGrid'
import { TemplateEditorTableCell } from './TemplateEditorTableCell'

interface FieldTraitTemplateEditorMainProps {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
  draftDetails: PracticeTemplateDetail[]
  onValueSave: (
    traitId: string,
    year: number,
    filledValues: { value?: unknown; input?: unknown; result?: unknown }
  ) => void
  onValueDelete: (traitId: string, year: number) => void
  onAddPractice: (traitId: string) => void
  onPrePopulate: (fieldId: string) => void
  fields: FieldModel[]
  canEdit: boolean
  canChangeOwnerScope: boolean
  canDelete: boolean
  isDeleting: boolean
  isSaving: boolean
  isDirty: boolean
  draftName: string
  nameError: string | null
  selectedId: string | 'new'
  onDraftNameChange: (name: string) => void
  onConfirmName: () => boolean
  onDelete: () => void
  onClose: () => void
  onSave: () => void
  ownerScope: 'farmer' | 'all'
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
  availableTraitIds: string[]
  shouldFocusName?: boolean
  onShouldFocusNameHandled?: () => void
}

export const FieldTraitTemplateEditorMain = ({
  task,
  draftDetails,
  onValueSave,
  onValueDelete,
  onAddPractice,
  onPrePopulate,
  fields,
  canEdit,
  canChangeOwnerScope,
  canDelete,
  isDeleting,
  isSaving,
  isDirty,
  draftName,
  nameError,
  selectedId,
  onDraftNameChange,
  onConfirmName,
  onDelete,
  onClose,
  onSave,
  ownerScope,
  onOwnerScopeChange,
  availableTraitIds,
  shouldFocusName,
  onShouldFocusNameHandled,
}: FieldTraitTemplateEditorMainProps) => {
  const { t } = useTranslation(['traits', '@cibo/programs/TemplateEditorModal'])
  const [_forceRender, setForceRender] = useState(0)
  const [prePopulateExpanded, setPrePopulateExpanded] = useState(false)
  const [prePopulateFieldId, setPrePopulateFieldId] = useState<string>('')

  const hasPractices = draftDetails.length > 0
  const showPrePopulate = canEdit && !hasPractices && fields.length > 0

  useEffect(() => {
    if (!showPrePopulate) setPrePopulateExpanded(false)
  }, [showPrePopulate])

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      setForceRender(prev => prev + 1)
    }, 50)
    return () => clearTimeout(timer)
  }, [hasPractices])

  const rows = useMemo(() => {
    const traitIds = uniq(draftDetails.map(d => d.traitId))
    return traitIds.map(traitId => {
      const d = draftDetails.find(detail => detail.traitId === traitId)
      const cellValue: Record<string, unknown> = {}
      if (d?.input !== undefined) cellValue.input = d.input
      if (d?.result !== undefined) cellValue.result = d.result
      if (d?.value !== undefined) cellValue.value = d.value
      return {
        id: traitId,
        traitId,
        '0': Object.keys(cellValue).length ? cellValue : undefined,
      }
    })
  }, [draftDetails])

  const renderValueCell = useCallback(
    (params: GridRenderCellParams) => (
      <TemplateEditorTableCell
        task={task as any}
        canEdit={canEdit}
        onValueSave={onValueSave}
        onValueDelete={onValueDelete}
        {...params}
      />
    ),
    [task, canEdit, onValueSave, onValueDelete]
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'traitId',
        headerName: t('practice', { ns: '@cibo/programs/TemplateEditorModal' }),
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }: GridRenderCellParams) => (
          <Stack direction="row" spacing={1} alignItems="center" width="100%">
            <Typography>{t(value, { ns: 'traits' })}</Typography>
          </Stack>
        ),
      },
      {
        field: '0',
        headerName: t('traitValue', { ns: '@cibo/programs/TemplateEditorModal' }),
        flex: 1,
        sortable: false,
        renderCell: renderValueCell,
      },
    ],
    [renderValueCell, t]
  )

  const handlePrePopulateClick = () => {
    setPrePopulateExpanded(true)
    setPrePopulateFieldId(fields[0]?.id ?? '')
  }

  const handlePrePopulateConfirm = () => {
    if (prePopulateFieldId) {
      onPrePopulate(prePopulateFieldId)
      setPrePopulateExpanded(false)
    }
  }

  const showFooter = canEdit || canDelete || selectedId === 'new'

  return (
    <Stack sx={{ flex: 1, overflow: 'hidden' }}>
      <TemplateNameHeader
        draftName={draftName}
        nameError={nameError}
        canEdit={canEdit}
        selectedId={selectedId}
        onDraftNameChange={onDraftNameChange}
        onConfirmName={onConfirmName}
        onClose={onClose}
        shouldFocusName={shouldFocusName}
        onShouldFocusNameHandled={onShouldFocusNameHandled}
      />

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {hasPractices ? (
          <TraitDataGrid rows={rows} columns={columns} hideFooter />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: 160,
              m: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('emptyStateFieldSelect', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {showPrePopulate && !prePopulateExpanded && (
              <Button
                variant="outlined"
                size="small"
                data-testid="template-pre-populate-button"
                onClick={handlePrePopulateClick}
              >
                {t('prePopulate', { ns: '@cibo/programs/TemplateEditorModal' })}
              </Button>
            )}
            {showPrePopulate && prePopulateExpanded && (
              <Stack spacing={1} alignItems="stretch" sx={{ width: '100%', maxWidth: 320, px: 1 }}>
                <Select
                  size="small"
                  fullWidth
                  value={prePopulateFieldId}
                  onChange={e => setPrePopulateFieldId(e.target.value)}
                  data-testid="template-pre-populate-field-select"
                >
                  {fields.map(f => (
                    <MenuItem key={f.id} value={f.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LandThumbnail field={f} width={32} height={32} />
                        <Stack>
                          <Typography>{f.name}</Typography>
                          <Typography variant="caption">
                            {t('acres', {
                              count: f.acres?.toFixed(1) ?? 0,
                              ns: '@cibo/landmanager/components',
                            })}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!prePopulateFieldId}
                  data-testid="template-pre-populate-confirm-button"
                  onClick={handlePrePopulateConfirm}
                >
                  {t('prePopulateConfirm', { ns: '@cibo/programs/TemplateEditorModal' })}
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Box>

      {showFooter && (
        <>
          <Divider />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
            {canEdit && (
              <>
                <AddPracticeMenu
                  availableTraitIds={availableTraitIds}
                  onAddPractice={onAddPractice}
                />
                {canChangeOwnerScope && (
                  <OwnerScopeSelect
                    ownerScope={ownerScope}
                    onOwnerScopeChange={onOwnerScopeChange}
                  />
                )}
              </>
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              color="error"
              data-testid="template-delete-button"
              loading={isDeleting}
              disabled={isDeleting || selectedId === 'new'}
              onClick={onDelete}
            >
              {t('deleteTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>

            <Button
              variant="contained"
              data-testid="template-save-button"
              loading={isSaving}
              disabled={!isDirty || isSaving || !hasPractices || !draftName.trim()}
              onClick={onSave}
            >
              {t('saveTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface TemplateNameHeaderProps {
  draftName: string
  nameError: string | null
  canEdit: boolean
  selectedId: string | 'new'
  onDraftNameChange: (name: string) => void
  onConfirmName: () => boolean
  onClose: () => void
  shouldFocusName?: boolean
  onShouldFocusNameHandled?: () => void
}

const TemplateNameHeader = ({
  draftName,
  nameError,
  canEdit,
  selectedId,
  onDraftNameChange,
  onConfirmName,
  onClose,
  shouldFocusName,
  onShouldFocusNameHandled,
}: TemplateNameHeaderProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const [isEditing, setIsEditing] = useState(selectedId === 'new')
  const [pendingFocus, setPendingFocus] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setIsEditing(selectedId === 'new')
  }, [selectedId])

  useEffect(() => {
    if (shouldFocusName) {
      setIsEditing(true)
      setPendingFocus(true)
      onShouldFocusNameHandled?.()
    }
  }, [shouldFocusName])

  useEffect(() => {
    if (pendingFocus && inputRef.current) {
      inputRef.current.focus()
      setPendingFocus(false)
    }
  })

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (onConfirmName()) setIsEditing(false)
    } else if (e.key === 'Escape' && selectedId !== 'new') {
      setIsEditing(false)
    }
  }

  const handleConfirm = () => {
    if (onConfirmName()) setIsEditing(false)
  }

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, minHeight: 48 }}>
      {isEditing ? (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '50%', minWidth: 0 }}>
          <TextField
            autoFocus
            inputRef={inputRef}
            size="small"
            label={t('templateName')}
            value={draftName}
            onChange={e => onDraftNameChange(e.target.value)}
            onKeyDown={handleKeyDown}
            error={!!nameError}
            helperText={nameError ?? undefined}
            disabled={!canEdit}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            data-testid="template-name-input"
          />
          <Button size="small" onClick={handleConfirm} data-testid="template-name-confirm-button">
            {t('save', { ns: '@cibo/programs/TemplateEditorModal' })}
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '50%', minWidth: 0 }}>
          <Typography
            data-testid="template-name-display"
            sx={{
              fontSize: 16,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {draftName || t('newTemplate')}
          </Typography>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => setIsEditing(true)}
              aria-label={t('editTemplateName')}
              data-testid="template-name-edit-button"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
      <Box sx={{ flex: 1 }} />
      <IconButton size="small" onClick={onClose} aria-label={t('close')}>
        <CloseIcon />
      </IconButton>
    </Stack>
  )
}

interface AddPracticeMenuProps {
  availableTraitIds: string[]
  onAddPractice: (traitId: string) => void
}

const AddPracticeMenu = ({ availableTraitIds, onAddPractice }: AddPracticeMenuProps) => {
  const { t } = useTranslation(['traits', '@cibo/programs/TemplateEditorModal'])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        data-testid="template-add-practice-button"
        disabled={availableTraitIds.length === 0}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      >
        {t('addPractice', { ns: '@cibo/programs/TemplateEditorModal' })}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {availableTraitIds.length === 0 ? (
          <MenuItem disabled>
            {t('allPracticesAdded', { ns: '@cibo/programs/TemplateEditorModal' })}
          </MenuItem>
        ) : (
          availableTraitIds.map(traitId => (
            <MenuItem
              key={traitId}
              onClick={() => {
                onAddPractice(traitId)
                setAnchorEl(null)
              }}
            >
              {t(traitId, { ns: 'traits' })}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  )
}

interface OwnerScopeSelectProps {
  ownerScope: 'farmer' | 'all'
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
}

const OwnerScopeSelect = ({ ownerScope, onOwnerScopeChange }: OwnerScopeSelectProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  return (
    <Select
      size="small"
      data-testid="template-owner-scope-select"
      value={ownerScope}
      onChange={e => onOwnerScopeChange(e.target.value as 'farmer' | 'all')}
      renderValue={value => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {value === 'farmer' ? <PersonIcon fontSize="small" /> : <PeopleIcon fontSize="small" />}
          <Typography variant="body2">
            {t(value === 'farmer' ? 'onlyThisFarmer' : 'allFarmers')}
          </Typography>
        </Stack>
      )}
    >
      <MenuItem value="farmer">
        <ListItemIcon>
          <PersonIcon fontSize="small" />
        </ListItemIcon>
        {t('onlyThisFarmer')}
      </MenuItem>
      <MenuItem value="all">
        <ListItemIcon>
          <PeopleIcon fontSize="small" />
        </ListItemIcon>
        {t('allFarmers')}
      </MenuItem>
    </Select>
  )
}
